/* eslint-disable */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { AiOutlineUser } from 'react-icons/ai';
import { BsPhone } from 'react-icons/bs';
import Loader from '../Loader';

const DEFAULT_LOGO =
  'https://s3.us-east-2.amazonaws.com/sc-prod-embedded.salescaptain.com/resources/2024-09-06/istockphoto-1280560319-612x612_1725598610728.jpg';

const DEFAULTS = {
  page_background_color: '#6a87e4',
  modal_background_color: '#ffffff',
  button_color: '#1942a7',
  button_text: 'Sign up',
  title: 'Get on our list!',
  description:
    'Want to get special offers and deals sent right to your phone? Subscribe today.',
  disclaimer_text:
    "By clicking 'Sign up,' you authorize us to send text messages with offers & other information, possibly using automated technology, to the number you provided. Message/data rates apply. Consent is not a condition of purchase. You may unsubscribe at any time.",
  full_name_placeholder: 'Full Name',
  mobile_placeholder: 'Mobile Phone',
  logo_url: DEFAULT_LOGO,
};

const fieldClass = 'qs-input pl-10';

const getInitials = (name) => (name || '')
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((word) => word[0])
  .join('')
  .toUpperCase();

function SignupForm({ baseURL }) {
  const { companyId } = useParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // Fall back to company initials when the logo fails to load
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    fetchSignupFormSettings();
  }, [companyId]);

  const fetchSignupFormSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${baseURL}/toolskit/fetch-signup-form-page-setting/${companyId}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      );
      if (res?.data?.status === 'success') {
        setSettings(res?.data?.data || res?.data);
      } else if (res?.data) {
        setSettings(res.data?.data || res.data);
      }
    } catch (err) {
      console.log('fetchSignupFormSettings err', err);
      toast.error('Unable to load signup form');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fullName.trim().length < 2) {
      toast.error('Please enter your full name');
      return;
    }
    if (mobile.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${baseURL}/toolskit/signup-form/${companyId}`,
        {
          full_name: fullName.trim(),
          phone: mobile.trim(),
        },
        {
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      );
      if (res?.data?.status === 'success') {
        setSubmitted(true);
        toast.success(res?.data?.message || 'Signed up successfully');
      } else {
        toast.error(res?.data?.error || 'Something went wrong');
      }
    } catch (err) {
      console.log('signup submit err', err);
      toast.error(err?.response?.data?.message || 'Unable to submit signup');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  const pageBg = settings?.page_background_color || DEFAULTS.page_background_color;
  const modalBg = settings?.modal_background_color || DEFAULTS.modal_background_color;
  const btnColor = settings?.button_color || DEFAULTS.button_color;
  const logoUrl = settings?.logo_url || DEFAULTS.logo_url;
  const title = settings?.title || DEFAULTS.title;
  const description = settings?.description || DEFAULTS.description;
  const buttonText = settings?.button_text || DEFAULTS.button_text;
  const disclaimerText = settings?.disclaimer_text || DEFAULTS.disclaimer_text;
  const fullNamePlaceholder = settings?.full_name_placeholder || DEFAULTS.full_name_placeholder;
  const mobilePlaceholder = settings?.mobile_placeholder || DEFAULTS.mobile_placeholder;
  const companyName = settings?.company_name || settings?.name;

  return (
    <div
      className="relative flex min-h-screen w-screen items-center justify-center overflow-hidden p-4 sm:p-8"
      style={{ backgroundColor: pageBg }}
    >
      {/* Decorative top-right wave */}
      <div
        className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-[45%] opacity-30"
        style={{ backgroundColor: '#ffffff', filter: 'blur(2px)' }}
      />
      <div
        className="pointer-events-none absolute -right-8 top-8 h-56 w-56 rounded-[40%] opacity-20"
        style={{ backgroundColor: '#1e3a8a' }}
      />

      {/* Decorative bottom-left dots */}
      <div className="pointer-events-none absolute bottom-6 left-6 grid grid-cols-6 gap-2 opacity-40 sm:bottom-10 sm:left-10">
        {Array.from({ length: 36 }).map((_, i) => (
          <span
            key={i}
            className="h-1.5 w-1.5 rounded-full bg-white/70 sm:h-2 sm:w-2"
          />
        ))}
      </div>

      <form
        onSubmit={handleSubmit}
        className="qs-fade-in relative z-10 w-full max-w-[440px] rounded-3xl px-6 py-8 shadow-2xl ring-1 ring-black/5 sm:px-9 sm:py-10"
        style={{ backgroundColor: modalBg }}
      >
        {submitted ? (
          <div className="flex flex-col items-center py-8 text-center">
            <h2 className="qs-title">You&apos;re on the list!</h2>
            <p className="qs-subtitle mt-3">
              Thanks for signing up. We&apos;ll be in touch soon.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center text-center">
              {!logoFailed && (
                <img
                  alt="logo"
                  src={logoUrl}
                  className="mb-5 h-16 max-w-[220px] object-contain"
                  onError={() => setLogoFailed(true)}
                />
              )}
              {logoFailed && companyName && (
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-xl font-semibold text-slate-700 shadow-md ring-1 ring-black/5">
                  {getInitials(companyName)}
                </div>
              )}
              <h2 className="qs-title">{title}</h2>
              <p className="qs-subtitle mt-2">{description}</p>
            </div>

            <div className="mt-8 flex flex-col gap-4">
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-400">
                  <AiOutlineUser size={18} />
                </span>
                <input
                  type="text"
                  className={fieldClass}
                  placeholder={fullNamePlaceholder}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                />
              </div>
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-400">
                  <BsPhone size={18} />
                </span>
                <input
                  type="tel"
                  className={fieldClass}
                  placeholder={mobilePlaceholder}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  autoComplete="tel"
                />
              </div>
            </div>

            <p className="mt-5 text-left text-[11px] leading-relaxed text-slate-500">
              {disclaimerText}
            </p>

            <button
              type="submit"
              disabled={submitting}
              className="qs-btn mt-5 text-white hover:opacity-95"
              style={{ backgroundColor: btnColor }}
            >
              {submitting ? 'Submitting...' : buttonText}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

export default SignupForm;
